import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { track } from "../lib/analytics";
import { getJourneyStep } from "../config/journeyConfig";
import { useAuth } from "../context/AuthContext";
import { getToken } from "../lib/auth";

export function MarketingNav() {
  const { user, logout } = useAuth();
  const items = [
    { label: "How it works", to: "/guide" },
    { label: "The journey", to: "/#journey" },
    { label: "About", to: "/about" },
    { label: "Membership", to: "/membership" },
  ];
  return (
    <header
      data-testid="marketing-nav"
      className="fixed top-0 inset-x-0 z-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-5">
        <div className="glass rounded-full px-4 md:px-6 py-3 flex items-center justify-between">
          <Logo size="sm" />
          <nav className="hidden md:flex items-center gap-7 text-sm text-white/70">
            {items.map((i) => (
              <a
                key={i.label}
                href={i.to}
                className="hover:text-white transition-colors"
                data-testid={`nav-${i.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {i.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="text-sm text-white/70 hover:text-white px-3 py-1.5 rounded-full transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  data-testid="nav-logout"
                  onClick={() => { track("logout_clicked", { page: "/" }); logout(); }}
                  className="text-sm text-white/70 hover:text-white px-3 py-1.5 rounded-full border border-white/15 hover:border-white/35 transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-signin"
                  onClick={() => track("login_clicked", { page: "/" })}
                  className="text-sm text-white/70 hover:text-white px-3 py-1.5 rounded-full transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/onboarding"
                  data-testid="nav-cta"
                  onClick={() => track("cta_start_clicked", { page: "/" })}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-gold text-[#080B14] text-sm font-semibold px-5 py-2 glow-gold-soft hover:scale-[1.02] transition-transform"
                >
                  Start Your Journey
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

const APP_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: "grid" },
  { to: "/discovery", label: "Discovery", icon: "search" },
  { to: "/presence",  label: "Presence",  icon: "presence" },
  { to: "/select-seven", label: "Your 7", icon: "star" },
  { to: "/bracket", label: "Bracket", icon: "bracket" },
  { to: "/round", label: "Rounds", icon: "flame" },
  { to: "/compatibility", label: "Compatibility", icon: "radar" },
  { to: "/chat", label: "Messages", icon: "chat" },
  { to: "/concierge", label: "Concierge", icon: "concierge" },
  { to: "/settings", label: "Profile", icon: "user" },
  { to: "/settings", label: "Settings", icon: "sliders" },
];

function NavIcon({ name }) {
  const base = "h-[18px] w-[18px] stroke-current";
  switch (name) {
    case "grid":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "search":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case "star":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="m12 3 2.8 6 6.2.9-4.5 4.4 1.1 6.3L12 17.7 6.4 20.6l1.1-6.3L3 10l6.2-.9L12 3Z" />
        </svg>
      );
    case "bracket":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="M4 5h4v14H4" />
          <path d="M20 5h-4v14h4" />
          <path d="M8 12h8" />
        </svg>
      );
    case "flame":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="M12 3c2 4-4 5 0 10a5 5 0 0 0 8-4c-1 0-2-1-2-3 0 0-2 2-3 0 0-2 0-3-3-3Z" />
        </svg>
      );
    case "radar":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3v18M3 12h18M5.5 5.5l13 13M18.5 5.5l-13 13" opacity=".35" />
        </svg>
      );
    case "chat":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="M4 5h16v11H8l-4 4V5Z" />
        </svg>
      );
    case "user":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6" />
        </svg>
      );
    case "presence":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <circle cx="12" cy="12" r="2.5" fill="currentColor" strokeWidth="0" fillOpacity="0.9" />
          <circle cx="12" cy="12" r="6.5" />
          <circle cx="12" cy="12" r="10.5" strokeOpacity="0.3" />
        </svg>
      );
    case "sliders":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="M4 7h10M4 12h6M4 17h14" />
          <circle cx="17" cy="7" r="2" />
          <circle cx="13" cy="12" r="2" />
          <circle cx="19" cy="17" r="2" />
        </svg>
      );
    case "shield":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="M12 3 4 7v5c0 4.4 3.4 8.5 8 9.5 4.6-1 8-5.1 8-9.5V7l-8-4Z" />
        </svg>
      );
    case "concierge":
      return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" className={base}>
          <path d="M4 18h16" />
          <path d="M12 3v2" />
          <path d="M5 10a7 7 0 0 1 14 0H5Z" />
          <path d="M3 18v-1a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v1" />
        </svg>
      );
    default:
      return null;
  }
}



export function AppShell({ children }) {
  const { logout, user } = useAuth();
  const { pathname } = useLocation();
  const journeyStep = getJourneyStep(pathname);
  const stage = journeyStep?.sidebar ?? null;

  // TODO: swap with real user-progress state once auth/backend is wired
  const canSeePresence =
    localStorage.getItem("bm_onboarding_complete") === "true" &&
    localStorage.getItem("bm_discovery_visited") === "true";

  let navItems = APP_NAV.filter(
    (item) => item.to !== "/presence" || canSeePresence
  );

  // ensure concierge always appears
  if (!navItems.find(i => i.to === "/concierge")) {
    navItems = [...navItems, { to: "/concierge", label: "Concierge", icon: "concierge" }];
  }

  // R2: sessionStorage resets when the tab/session closes — no module-level
  // flag needed. Guards against firing on every re-mount caused by routing.
  useEffect(() => {
    if (canSeePresence && !sessionStorage.getItem("bm_presence_nav_impression_fired")) {
      track("presence_nav_impression", { source: "nav", surfaced: true });
      sessionStorage.setItem("bm_presence_nav_impression_fired", "true");
    }
  }, [canSeePresence]);

  // R1: fire once per device the first time the Presence gate becomes true.
  useEffect(() => {
    if (
      canSeePresence &&
      localStorage.getItem("bm_presence_unlocked_tracked") !== "true"
    ) {
      track("presence_unlocked", { source: "progressive_reveal" });
      localStorage.setItem("bm_presence_unlocked_tracked", "true");
    }
  }, [canSeePresence]);

  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const loadUnread = () => {
      fetch("/api/concierge/unread-count", { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.ok ? r.json() : { count: 0 })
        .then((d) => setUnread(d.count || 0))
        .catch(() => {});
    };
    loadUnread();
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="app-shell"
      className="min-h-screen grid md:grid-cols-[260px_1fr] bg-[#05070d]"
    >
      <aside className="hidden md:flex flex-col border-r border-white/5 bg-[#070a12]/70 sticky top-0 h-screen">
        <div className="px-6 pt-7 pb-5">
          <Logo size="md" />
        </div>
        <div className="px-4 text-[10px] tracking-[0.24em] uppercase text-white/35 mb-2 pl-5">
          Journey
        </div>
        <nav className="flex-1 px-3 flex flex-col gap-0.5 text-sm">
          {navItems.map((item) => {
            const active =
              pathname === item.to ||
              (item.to !== "/" && pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={item.to === "/presence" ? () => track("presence_nav_click") : undefined}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  active
                    ? "bg-white/5 text-white ring-1 ring-[rgba(212,175,55,0.25)]"
                    : "text-white/60 hover:text-white hover:bg-white/[0.03]"
                }`}
              >
                <span
                  className={`transition-colors ${active ? "text-[#D4AF37]" : "text-white/40 group-hover:text-white/70"}`}
                >
                  <NavIcon name={item.icon} />
                </span>
                <span>{item.label}</span>
                {item.to === "/concierge" && unread > 0 ? (
                  <span className="ml-auto min-w-[18px] h-[18px] rounded-full bg-[#D4AF37] text-[#080B14] text-[10px] font-bold flex items-center justify-center px-1">
                    {unread}
                  </span>
                ) : active ? (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                ) : null}
              </Link>
            );
          })}
        </nav>
        {stage && (
          <div className="p-4 m-3 rounded-2xl glass">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-1.5">
              {stage.label}
            </div>
            <div className="font-display text-lg leading-tight mb-2">
              {stage.name}
            </div>
            <div className="text-xs text-white/55 mb-3">
              {stage.sub}
            </div>
            <Link
              to={stage.to}
              data-testid="sidebar-continue-round"
              className="text-xs font-semibold text-[#080B14] bg-gradient-gold rounded-full px-3.5 py-1.5 inline-flex items-center gap-1.5"
            >
              Advance
              <span aria-hidden>→</span>
            </Link>
          </div>
        )}
      
          {user?.role === "admin" && (
            <div className="px-3 mb-1">
              <div className="px-2 text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]/60 mb-1">Admin</div>
              {[
                { to: "/admin/users",    label: "Users",    icon: "shield" },
                { to: "/admin/metrics",  label: "Metrics",  icon: "radar"    },
                { to: "/admin/heatmap",    label: "Heatmap",    icon: "presence" },
                { to: "/admin/analytics", label: "Analytics",  icon: "radar"    },
                { to: "/admin/messages",  label: "Messages",   icon: "chat"     },
              ].map((item) => {
                const active = pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`admin-nav-${item.label.toLowerCase()}`}
                    className={`group flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all ${
                      active
                        ? "bg-white/5 text-white ring-1 ring-[rgba(212,175,55,0.25)]"
                        : "text-white/60 hover:text-white hover:bg-white/[0.03]"
                    }`}
                  >
                    <span className={`transition-colors ${ active ? "text-[#D4AF37]" : "text-white/40 group-hover:text-white/70" }`}>
                      <NavIcon name={item.icon} />
                    </span>
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
          <div className="p-4 m-3">
            <button
              onClick={() => { track("logout_clicked", { page: typeof window !== "undefined" ? window.location.pathname : "/" }); logout(); }}
              className="w-full text-sm text-white/60 hover:text-white border border-white/10 rounded-xl py-2 transition"
            >
              Sign out
            </button>
          </div>
        </aside>

      <main className="min-w-0">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-3 inset-x-3 z-40 glass rounded-full px-2 py-2 flex items-center justify-between">
        {navItems.slice(0, 5).map((item) => {
          const active = pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex-1 flex flex-col items-center gap-0.5 py-1 rounded-full ${active ? "text-[#D4AF37]" : "text-white/60"}`}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <span className="relative">
                <NavIcon name={item.icon} />
                {item.to === "/concierge" && unread > 0 && (
                  <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-[#D4AF37] text-[#080B14] text-[8px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </span>
              <span className="text-[9px] tracking-wider uppercase">
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
